import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Group,
  Stack,
  Text,
  Button,
  Progress,
  ThemeIcon,
  Badge,
  Loader,
  Menu,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { FileText, Mail, ExternalLink, MoreVertical } from 'react-feather';
import { useEmailToggle } from '../../../hooks';
import { intakeApi, type IntakeStats } from '../../../services/api/intake';
import { formatDateOnly } from '../../../utils/formatters';
import { ComposeIntakeEmailModal } from './ComposeIntakeEmailModal';
import { SendReminderModal } from './SendReminderModal';
import { notifications } from '@mantine/notifications';

interface IntakeBannerProps {
  cohortId: string;
  cohortName: string;
  onIntakeSent?: () => void;
  // MED-778: When false, hide Send via SGOS and Resend buttons
  participantFeaturesEnabled?: boolean;
}

export function IntakeBanner({ cohortId, cohortName, onIntakeSent, participantFeaturesEnabled = true }: IntakeBannerProps) {
  const navigate = useNavigate();
  const [stats, setStats] = useState<IntakeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [composeModalOpened, { open: openComposeModal, close: closeComposeModal }] =
    useDisclosure(false);
  const [reminderModalOpened, { open: openReminderModal, close: closeReminderModal }] =
    useDisclosure(false);

  // MED-579: Email toggle gating
  const { isEnabled: isEmailEnabled } = useEmailToggle(cohortId);
  // MED-778: Feature toggle supersedes per-feature email toggles
  const isIntakeEmailDisabled = !participantFeaturesEnabled || !isEmailEnabled('participants', 'intake');
  const emailDisabledTooltip = !participantFeaturesEnabled
    ? 'Participant features are disabled for this cohort. Re-enable in Cohort Settings > Audience Feature Controls.'
    : 'This email feature has been turned off for this cohort. To enable it, go to Cohort Settings > Email Settings.';

  const fetchStats = useCallback(async () => {
    try {
      const data = await intakeApi.getStats(cohortId);
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch intake stats:', error);
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleIntakeSent = async () => {
    closeComposeModal();
    // Refresh both the banner stats and the participant table
    await Promise.all([
      fetchStats(),
      onIntakeSent?.(),
    ]);
    notifications.show({
      title: 'Matching Forms Sent',
      message: 'Matching form emails have been queued for sending',
      color: 'green',
    });
  };

  const handleRemindersSent = async () => {
    closeReminderModal();
    // Refresh both the banner stats and the participant table
    await Promise.all([
      fetchStats(),
      onIntakeSent?.(), // Also refresh participant table since reminder status may have changed
    ]);
    notifications.show({
      title: 'Reminders Sent',
      message: 'Reminder emails have been queued for sending',
      color: 'green',
    });
  };

  if (loading) {
    return (
      <Card withBorder p="md">
        <Group justify="center" py="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading matching form status...</Text>
        </Group>
      </Card>
    );
  }

  // MED-865 FR4: Gate the progress view on intake_configured_at rather than
  // intake_form_id. The two are typically set together via the configure flow,
  // but `intake_configured_at` is the explicit "this cohort is ready to collect
  // intake" timestamp the spec calls out as the trigger for the progress
  // denominator (all participants in the cohort, not just SGOS-sent ones).
  const isConfigured = stats?.intake_configured_at != null;

  // Pre-configure state: No form configured yet
  if (!stats || (!isConfigured && stats.sent === 0)) {
    const participantCount = stats?.total ?? 0;
    const participantText = participantCount > 0
      ? `from all ${participantCount} participants`
      : 'from your participants';

    return (
      <Card withBorder p="md" bg="var(--medley-bg-surface)" radius={8} style={{ boxShadow: 'none' }}>
        <Group justify="space-between" align="center" wrap="nowrap" gap="md">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size={48} radius={48} color="surveyPurple" variant="light" style={{ flexShrink: 0 }}>
              <FileText size={24} />
            </ThemeIcon>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={600}>Matching Form not configured</Text>
              <Text size="sm" c="dimmed">
                Configure the Matching Form to gather participant preferences {participantText} for coach matching.
              </Text>
            </Stack>
          </Group>
          <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
            <Button
              onClick={() => navigate(`?tab=surveys&survey=matching_form`)}
            >
              Configure in Surveys tab
            </Button>
          </Group>
        </Group>
      </Card>
    );
  }

  // Post-configure state: Form configured (with or without SGOS send), show progress
  const completionPercentage = stats.total > 0
    ? Math.round((stats.completed / stats.total) * 100)
    : 0;
  const pendingCount = stats.sent - stats.completed;

  // Determine distribution status. MED-865: Once email-fallback completions
  // start arriving via the partner-sent flow, stats.sent stays 0 (no SGOS
  // sends) but stats.completed grows. Without the completed-count guard the
  // banner would say "Awaiting external distribution" indefinitely even after
  // responses are flowing in. This keeps the "awaiting" state limited to the
  // genuine pre-submission window.
  const isAwaitingExternalDistribution =
    stats.sent === 0 && stats.completed === 0 && isConfigured;

  // Format sent date: relative if recent, absolute if older
  const formatSentDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Configured';
    const sentDate = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - sentDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) {
      return 'Sent just now';
    } else if (diffHours < 24) {
      const hours = Math.floor(diffHours);
      return `Sent ${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else {
      // Format as "Sent Jan 8, 2025"
      return `Sent ${sentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
  };

  // Format due date
  const formatDueDate = (dateStr: string | null): string | null => {
    if (!dateStr) return null;
    return `Due ${formatDateOnly(dateStr)}`;
  };

  // Status text based on distribution and completion
  const statusText = isAwaitingExternalDistribution
    ? 'Awaiting external distribution'
    : completionPercentage === 0
      ? 'Waiting for responses'
      : 'Responses update automatically';

  const sentDateText = isAwaitingExternalDistribution
    ? null
    : formatSentDate(stats.intake_configured_at);
  const dueDateText = formatDueDate(stats.intake_due_date);
  const descriptionParts = [sentDateText, dueDateText, statusText].filter(Boolean);
  const descriptionText = descriptionParts.join(' • ');

  return (
    <>
      <Card withBorder p="md" bg="var(--medley-bg-surface)" radius={8} style={{ boxShadow: 'none' }}>
        <Group justify="space-between" align="flex-start">
          <Group gap="md" align="flex-start">
            <ThemeIcon size={48} radius={48} color="surveyPurple" variant="light">
              <FileText size={24} />
            </ThemeIcon>
            <Stack gap={4}>
              <Group gap="xs">
                <Text fw={600}>
                  {isAwaitingExternalDistribution ? 'Matching Form Configured' : 'Matching Form Responses'}
                </Text>
                <Badge size="sm" tt="none" styles={{ root: { backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8B5CF6', border: 'none' } }}>
                  {isAwaitingExternalDistribution ? 'Collecting' : 'Paperform'}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {descriptionText}
              </Text>
              <Group gap="sm" wrap="nowrap" align="center" mt={4}>
                <Progress
                  value={completionPercentage}
                  size="sm"
                  radius="sm"
                  color="surveyPurple"
                  style={{ flex: 1 }}
                  miw={300}
                />
                <Text size="sm" c="surveyPurple" fw={500} style={{ whiteSpace: 'nowrap' }}>
                  {stats.completed} of {stats.total} completed ({completionPercentage}%)
                </Text>
              </Group>
            </Stack>
          </Group>

          <Group gap="sm">
            {isAwaitingExternalDistribution ? (
              <>
                <Button
                  variant="default"
                  leftSection={<ExternalLink size={16} />}
                  component="a"
                  href="https://paperform.co/dashboard/450432"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View in Paperform
                </Button>
                <Tooltip label={emailDisabledTooltip} disabled={!isIntakeEmailDisabled} multiline w={280}>
                  <Button leftSection={<Mail size={16} />} onClick={openComposeModal} disabled={isIntakeEmailDisabled}>
                    Send via SGOS
                  </Button>
                </Tooltip>
              </>
            ) : (
              <>
                {(pendingCount > 0 || stats.not_sent > 0) && (
                  <Tooltip label={emailDisabledTooltip} disabled={!isIntakeEmailDisabled} multiline w={280}>
                    <Button
                      variant="outline"
                      color="gray"
                      bg="white"
                      style={{ borderColor: '#E8E6E3' }}
                      leftSection={<Mail size={16} />}
                      onClick={openReminderModal}
                      disabled={isIntakeEmailDisabled}
                    >
                      Resend Matching Form
                    </Button>
                  </Tooltip>
                )}
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="lg">
                      <MoreVertical size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<ExternalLink size={14} />}
                      component="a"
                      href="https://paperform.co/dashboard/450432"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View in Paperform
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </>
            )}
          </Group>
        </Group>
      </Card>

      <ComposeIntakeEmailModal
        opened={composeModalOpened}
        onClose={closeComposeModal}
        cohortId={cohortId}
        cohortName={cohortName}
        onSent={handleIntakeSent}
      />

      <SendReminderModal
        opened={reminderModalOpened}
        onClose={closeReminderModal}
        cohortId={cohortId}
        onSent={handleRemindersSent}
      />
    </>
  );
}
